<!--
  Copyright (C) 2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="workspace-list">
    <PrimeDataView
      :value="workspaces"
      :layout="layout"
      :paginator="paginator"
      :rows="rows"
      :rowsPerPageOptions="rowsPerPageOptions"
    >
      <template #empty>
        <div class="workspace-list-empty">
          <slot name="empty">
            <p>{{ emptyMessage }}</p>
          </slot>
        </div>
      </template>

      <template #grid="slotProps">
        <div class="workspace-grid">
          <div
            v-for="workspace in slotProps.items"
            :key="workspace.id || workspace.guid || workspace.name"
            class="workspace-grid-item"
          >
            <slot name="card" :workspace="workspace">
              <WorkspaceCard
                :workspace="workspace"
                :showDescription="showDescription"
                :showVisibility="showVisibility"
                :showRole="showRole"
                :showActionMenu="showActionMenu"
                :actionMenuTooltip="actionMenuTooltip"
                :noDescriptionText="noDescriptionText"
                :roleLabels="roleLabels"
                :visibilityLabels="visibilityLabels"
                :roleTooltips="roleTooltips"
                @click="$emit('workspace-click', workspace)"
                @action-menu="handleActionMenu"
              >
                <template #actions>
                  <slot name="card-actions" :workspace="workspace"></slot>
                </template>
                <template #meta>
                  <slot name="card-meta" :workspace="workspace"></slot>
                </template>
                <template #footer-end>
                  <slot name="card-footer-end" :workspace="workspace"></slot>
                </template>
              </WorkspaceCard>
            </slot>
          </div>
        </div>
      </template>

      <template #list="slotProps">
        <div class="workspace-list-view">
          <div
            v-for="workspace in slotProps.items"
            :key="workspace.id || workspace.guid || workspace.name"
            class="workspace-list-item"
          >
            <slot name="list-item" :workspace="workspace">
              <WorkspaceCard
                :workspace="workspace"
                :showDescription="showDescription"
                :showVisibility="showVisibility"
                :showRole="showRole"
                :showActionMenu="showActionMenu"
                :actionMenuTooltip="actionMenuTooltip"
                :noDescriptionText="noDescriptionText"
                :roleLabels="roleLabels"
                :visibilityLabels="visibilityLabels"
                :roleTooltips="roleTooltips"
                @click="$emit('workspace-click', workspace)"
                @action-menu="handleActionMenu"
              >
                <template #actions>
                  <slot name="card-actions" :workspace="workspace"></slot>
                </template>
                <template #meta>
                  <slot name="card-meta" :workspace="workspace"></slot>
                </template>
                <template #footer-end>
                  <slot name="card-footer-end" :workspace="workspace"></slot>
                </template>
              </WorkspaceCard>
            </slot>
          </div>
        </div>
      </template>
    </PrimeDataView>

    <!-- Action Menu -->
    <PrimeMenu ref="actionMenu" :model="currentActionMenuItems" :popup="true" />
  </div>
</template>

<script>
import DataView from "primevue/dataview";
import Menu from "primevue/menu";
import WorkspaceCard from "./WorkspaceCard.vue";

export default {
  name: "WorkspaceList",
  components: {
    PrimeDataView: DataView,
    PrimeMenu: Menu,
    WorkspaceCard,
  },
  props: {
    /**
     * Array of workspace objects
     */
    workspaces: {
      type: Array,
      default: () => [],
    },
    /**
     * Display layout: 'grid' or 'list'
     */
    layout: {
      type: String,
      default: "grid",
      validator: (value) => ["grid", "list"].includes(value),
    },
    /**
     * Enable pagination
     */
    paginator: {
      type: Boolean,
      default: true,
    },
    /**
     * Number of rows per page
     */
    rows: {
      type: Number,
      default: 12,
    },
    /**
     * Options for rows per page dropdown
     */
    rowsPerPageOptions: {
      type: Array,
      default: () => [6, 12, 24, 48],
    },
    /**
     * Message when list is empty
     */
    emptyMessage: {
      type: String,
      default: "Aucun espace trouvé",
    },
    /**
     * Show description in cards
     */
    showDescription: {
      type: Boolean,
      default: true,
    },
    /**
     * Show visibility icon in cards
     */
    showVisibility: {
      type: Boolean,
      default: true,
    },
    /**
     * Show role badge in cards
     */
    showRole: {
      type: Boolean,
      default: true,
    },
    /**
     * Show action menu button in cards
     */
    showActionMenu: {
      type: Boolean,
      default: true,
    },
    /**
     * Tooltip for action menu button
     */
    actionMenuTooltip: {
      type: String,
      default: "Actions",
    },
    /**
     * Custom role labels mapping
     */
    roleLabels: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Custom visibility labels mapping
     */
    visibilityLabels: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Text shown when no description
     */
    noDescriptionText: {
      type: String,
      default: "Aucune description",
    },
    /**
     * Custom role tooltips mapping (explains what the role means)
     */
    roleTooltips: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Action menu items generator function
     * Receives workspace as parameter, should return array of menu items
     */
    actionMenuItemsBuilder: {
      type: Function,
      default: null,
    },
  },
  emits: ["workspace-click", "action-menu"],
  data() {
    return {
      currentActionMenuItems: [],
      currentWorkspace: null,
    };
  },
  methods: {
    handleActionMenu(event, workspace) {
      this.currentWorkspace = workspace;

      if (this.actionMenuItemsBuilder) {
        this.currentActionMenuItems = this.actionMenuItemsBuilder(workspace);
        this.$refs.actionMenu.toggle(event);
      } else {
        this.$emit("action-menu", event, workspace);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.workspace-list {
  width: 100%;

  :deep(.p-dataview) {
    border: none;
    background: transparent;
  }

  :deep(.p-dataview-content) {
    background: transparent;
    border: none;
  }

  :deep(.p-paginator) {
    border: none;
    background: transparent;
  }

  :deep(.p-dataview-paginator-bottom) {
    border: none;
  }
}

.workspace-list-empty {
  text-align: center;
  padding: 2rem;
  color: var(--p-text-muted-color);
}

.workspace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
  padding: 0.5rem 0;
}

.workspace-grid-item {
  min-width: 0;
}

.workspace-list-view {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.workspace-list-item {
  width: 100%;
}

// Responsive adjustments
@media (max-width: 640px) {
  .workspace-grid {
    grid-template-columns: 1fr;
  }
}
</style>
